import React, {useEffect, useMemo, useState} from 'react';
import {Box, InputAdornment, SxProps, TextField, Typography, TypographyProps} from '@mui/material';
import {alpha, useTheme} from '@mui/material/styles';
import {parseNumber} from "@/utils/dataUtils";

export type NumberFieldType = 'integer' | 'float' | 'integer_with_leading_zeros';

export interface NumberFieldProps {
    label?: string;
    value: number | string | null | undefined;
    /** integer | float */
    type?: NumberFieldType;
    /** decimal places for float type, default 0 */
    decimalPlaces?: number;
    /** compact style: small font and tight spacing (default true) */
    compact?: boolean;
    /** text alignment: left | center | right (default 'right') */
    align?: 'left' | 'center' | 'right';
    /** deprecated: use align instead */
    alignRight?: boolean;
    /** typography variant override */
    variant?: TypographyProps['variant'];
    /** color override */
    color?: TypographyProps['color'];
    /** sx override */
    sx?: SxProps;
    /** editable mode toggles between display and input */
    editable?: boolean;
    /**
     * Keep TextField chrome but block typing (display-inside-input).
     * Ignored when editable is false (Typography path).
     */
    readOnly?: boolean;
    /** called with numeric value on blur/change */
    onChangeNumber?: (num: number) => void;
    /** called with display string (possibly formatted with parentheses) on blur/change */
    onChangeDisplay?: (display: string) => void;
    /** placeholder for input */
    placeholder?: string;
    /** optional prefix to show before the number (display and editable) */
    prefix?: React.ReactNode;
    /** optional suffix to show after the number (display and editable) */
    suffix?: React.ReactNode;
}

function formatWithParen(n: number, formatter: Intl.NumberFormat): string {
    const abs = Math.abs(n);
    const formatted = formatter.format(abs);
    return n < 0 ? `(${formatted})` : formatted;
}

export function NumberField({
                                label = '',
                                value,
                                type = 'integer',
                                decimalPlaces = 0,
                                compact = true,
                                align,
                                alignRight,
                                variant,
                                color,
                                sx,
                                editable = false,
                                readOnly = false,
                                onChangeNumber,
                                onChangeDisplay,
                                placeholder,
                                prefix,
                                suffix,
                            }: NumberFieldProps) {
    const theme = useTheme();
    const resolvedAlign: 'left' | 'center' | 'right' = align ?? (alignRight === undefined ? 'right' : (alignRight ? 'right' : 'left'));

    const maxFraction = type === 'float' ? Math.max(0, decimalPlaces) : 0;
    const keepLeadingZeros = type === 'integer_with_leading_zeros';

    const formatter = useMemo(() => new Intl.NumberFormat(undefined, {
        minimumFractionDigits: maxFraction,
        maximumFractionDigits: maxFraction,
    }), [maxFraction]);

    const num = parseNumber(value);
    const formattedDisplay = formatWithParen(num, formatter);

    const [draft, setDraft] = useState<string>(
        typeof value === 'string' ? String(value) : (editable ? (num < 0 ? `-${Math.abs(num)}` : `${num}`) : formattedDisplay)
    );
    const [isComposing, setIsComposing] = useState<boolean>(false);

    // Keep internal draft in sync with external value/formatter changes
    useEffect(() => {
        if (isComposing) return;
        const n = parseNumber(value);
        const display = editable
            ? (n < 0 ? `-${Math.abs(n)}` : `${n}`)
            : formatWithParen(n, formatter);
        setDraft(typeof value === 'string' ? String(value) : display);
    }, [value, formatter, editable, isComposing]);

    // If editable is turned off while the user is still focused (no blur),
    // immediately format current draft into display with parentheses and notify parent
    useEffect(() => {
        if (editable) return;
        // derive from current draft to avoid losing user's latest typed value
        let n = parseNumber(draft);
        // honor integer type by truncating decimals
        if (type === 'integer') n = Math.trunc(n);
        const pretty = formatWithParen(n, formatter);
        setDraft(pretty);
        onChangeNumber?.(n);
        onChangeDisplay?.(pretty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editable, formatter]);

    if (!editable) {
        const alignItems = resolvedAlign === 'left' ? 'flex-start' : (resolvedAlign === 'center' ? 'center' : 'flex-end');
        return (
            <Box
                sx={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems,
                    whiteSpace: 'nowrap',
                    ...sx,
                }}
            >
                {label ? (
                    <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1, mb: 0.25}}>
                        {label}
                    </Typography>
                ) : null}
                <Typography
                    component="span"
                    variant={variant || (compact ? 'body2' : 'body1')}
                    color={color}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        paddingRight: 14,
                        gap: 0.25,
                        ...(resolvedAlign ? {textAlign: resolvedAlign as any} : {}),
                        ...(compact ? {lineHeight: 1.2} : {}),
                    }}
                >
                    {prefix ? (
                        <Box
                            component="span"
                            sx={{
                                mr: 0.25,
                                opacity: 0.9,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {prefix}
                        </Box>
                    ) : null}
                    <Box component="span">{formattedDisplay}</Box>
                    {suffix ? (
                        <Box
                            component="span"
                            sx={{
                                ml: 0.25,
                                opacity: 0.9,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            {suffix}
                        </Box>
                    ) : null}
                </Typography>
            </Box>
        );
    }

    const sanitizeAndLimit = (raw: string): { normalized: string; number: number } => {
        let s = raw.replace(/,/g, '').trim();
        // if parentheses provided by user, interpret as negative
        let isParenNegative = false;
        if (/^\(.*\)$/.test(s)) {
            isParenNegative = true;
            s = s.slice(1, -1);
        }
        let neg = false;
        if (s.startsWith('-')) {
            neg = true;
            s = s.slice(1);
        }
        s = s.replace(/[^0-9.]/g, '');
        const firstDot = s.indexOf('.');
        if (firstDot >= 0) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');

        let [intPart, decPart] = s.split('.') as [string, string | undefined];
        if (!intPart) intPart = '0';
        if (type === 'float' && decPart) decPart = decPart.slice(0, maxFraction);
        if (type === 'integer' || type === 'integer_with_leading_zeros') decPart = undefined; // force integer

        const next = `${(neg || isParenNegative) ? '-' : ''}${intPart}${firstDot >= 0 && type === 'float' ? '.' : ''}${decPart ?? ''}`;
        const n = Number(next === '' || next === '-' || next === '.' || next === '-.' ? 0 : next);
        return {normalized: next, number: Number.isFinite(n) ? n : 0};
    };

    const handleBlur = () => {
        const {normalized, number} = sanitizeAndLimit(draft ?? '');
        const display = keepLeadingZeros ? normalized : formatWithParen(number, formatter);
        setDraft(display);
        onChangeNumber?.(number);
        onChangeDisplay?.(display);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let raw = e.target.value ?? '';
        if (isComposing) {
            setDraft(raw);
            return;
        }
        if (raw === '') {
            setDraft('');
            onChangeDisplay?.('');
            return;
        }
        // If previous draft was an auto '0' or '-0', remove the leading zero so the first user digit replaces it
        // Skip this behavior when keeping leading zeros is requested
        if (!keepLeadingZeros) {
            if (draft === '0' && /^0\d/.test(raw)) {
                raw = raw.replace(/^0+/, '');
            } else if (draft === '-0' && /^-0\d/.test(raw)) {
                raw = '-' + raw.replace(/^-0+/, '');
            }
        }
        const {normalized} = sanitizeAndLimit(raw);
        setDraft(normalized);
        onChangeDisplay?.(normalized);
    };

    const handleCompositionStart = () => setIsComposing(true);
    const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
        setIsComposing(false);
        const target = e.target as HTMLInputElement;
        const fakeEvent = {target} as unknown as React.ChangeEvent<HTMLInputElement>;
        handleChange(fakeEvent);
    };

    const handleFocus = () => {
        if (!editable) return;
        // When entering the field, convert any parentheses/pretty format into raw editable negative form
        const n = parseNumber(draft || value);
        // If current numeric value is 0, clear the input to let user start from empty
        if (n === 0) {
            setDraft('');
            onChangeDisplay?.('');
            return;
        }
        // Build raw editable string without thousand separators; keep '-' for negatives
        let raw = `${Math.abs(n)}`;
        if (type === 'integer') {
            // remove any decimals for integer type
            const dot = raw.indexOf('.');
            if (dot >= 0) raw = raw.slice(0, dot);
        } else if (type === 'float' && maxFraction >= 0) {
            // trim to at most maxFraction decimals (do not pad with zeros)
            const [i, d] = raw.split('.') as [string, string | undefined];
            raw = d ? `${i}.${d.slice(0, maxFraction)}` : i;
        }
        const edit = (n < 0 ? '-' : '') + raw;
        setDraft(edit);
        onChangeDisplay?.(edit);
    };

    return (
        <TextField
            label={label}
            value={draft}
            onChange={readOnly ? undefined : handleChange}
            onBlur={readOnly ? undefined : handleBlur}
            onFocus={readOnly ? undefined : handleFocus}
            onCompositionStart={readOnly ? undefined : handleCompositionStart}
            onCompositionEnd={readOnly ? undefined : handleCompositionEnd}
            placeholder={placeholder}
            size={compact ? 'small' : 'medium'}
            variant="outlined"
            inputProps={{
                readOnly: readOnly || undefined,
                style: {
                    textAlign: resolvedAlign,
                    fontSize: (theme.typography as any).body2?.fontSize || undefined,
                    lineHeight: 1.2,
                    paddingTop: 4,
                    paddingBottom: 4,
                    paddingRight: 14,
                },
                inputMode: type === 'float' ? 'decimal' : 'numeric',
            }}
            InputProps={{
                readOnly: readOnly || undefined,
                startAdornment: prefix ? (
                    <InputAdornment
                        position="start"
                        sx={{
                            mr: 0,
                            px: 0.1,
                            flexBasis: '20%',
                            maxWidth: '20%',
                            flexShrink: 0,
                            justifyContent: 'center',
                            alignItems: 'center',
                            '&.MuiInputAdornment-root': {display: 'inline-flex'},
                        }}
                    >
                        <Box component="span" sx={{fontSize: '0.80em'}}>{prefix}</Box>
                    </InputAdornment>
                ) : undefined,
                endAdornment: suffix ? (
                    <InputAdornment
                        position="end"
                        sx={{
                            ml: 0,
                            px: 0.1,
                            flexBasis: '5%',
                            maxWidth: '5%',
                            flexShrink: 0,
                            justifyContent: 'center',
                            alignItems: 'center',
                            '&.MuiInputAdornment-root': {display: 'inline-flex'},
                        }}
                    >
                        <Box component="span" sx={{fontSize: '0.80em'}}>{suffix}</Box>
                    </InputAdornment>
                ) : undefined,
            }}
            sx={{
                minWidth: 110,
                ...(compact ? {lineHeight: 1.2} : {}),
                '& .MuiOutlinedInput-root': {
                    backgroundColor: alpha(theme.palette.primary.main, readOnly ? 0.03 : 0.06),
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: readOnly ? theme.palette.divider : theme.palette.primary.main,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: readOnly ? theme.palette.divider : theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: readOnly ? theme.palette.divider : theme.palette.primary.main,
                },
                '& .MuiInputBase-input': {
                    backgroundColor: alpha(theme.palette.primary.main, readOnly ? 0.03 : 0.06),
                    cursor: readOnly ? 'default' : undefined,
                },
                ...sx,
            }}
        />
    );
}
